import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import BookingCalendar from "../components/calendar/BookingCalendar";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { useAuth } from "../auth/authStore";
import { useNotifications } from "../notifications/notificationsStore";

const PRIVILEGED_ROLES = new Set(["ADMIN", "PSYCHOLOGIST", "MODERATOR"]);

function firstName(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)[0] || "коллега";
}

function roleLabel(value) {
  const role = String(value || "").replace(/^ROLE_/, "").toUpperCase();
  if (role === "ADMIN") return "Администратор";
  if (role === "PSYCHOLOGIST") return "Психолог";
  if (role === "MODERATOR") return "Модератор";
  if (role === "CLIENT") return "Клиент";
  return "Пользователь";
}

export default function PsychologistCalendar() {
  const navigate = useNavigate();
  const { me, role, logout } = useAuth();
  const { unreadNotificationsCount = 0 } = useNotifications();
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

  const normalizedRole = String(role || "").replace(/^ROLE_/, "").toUpperCase();
  const canOpenLiteratureEditor = PRIVILEGED_ROLES.has(normalizedRole);
  const userName = me?.name || me?.fullName || me?.login || me?.email || "Пользователь";
  const psychologist = React.useMemo(() => {
    const id = Number(me?.id) || null;
    if (!id) return null;
    return {
      id,
      name: me?.name || me?.fullName || me?.login || me?.email || `Психолог #${id}`,
      image: me?.avatarUrl || me?.avatar || null,
    };
  }, [me]);

  const greetingDate = React.useMemo(() => {
    return new Intl.DateTimeFormat("ru-RU", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date());
  }, []);

  const handleLogout = React.useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  return (
    <div className="psych-dashboard-v2 client-dashboard-v2 podcasts-hub">
      <Helmet prioritizeSeoTags>
        <title>Календарь записей | Бюро Души</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="shell shell--shared podcasts-shell">
        <AppSidebarV2
          activeKey="dashboard"
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          userName={userName}
          roleLabel={roleLabel(role)}
          canOpenLiteratureEditor={canOpenLiteratureEditor}
          notificationsBadge={unreadNotificationsCount}
          onLogout={handleLogout}
        />

        <button
          className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
          type="button"
          onClick={() => setMobileSidebarOpen(false)}
          aria-label="Закрыть меню"
        />

        <main className="main podcasts-main cd-main">
          <div className="topbar">
            <div className="topbar-left">
              <button
                type="button"
                className="btn-icon topbar-hamburger"
                onClick={() => setMobileSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              <div>
                <div className="topbar-greeting">Календарь записей, {firstName(userName)} 👋</div>
                <div className="topbar-date">{greetingDate}</div>
              </div>
            </div>

            <div className="topbar-right">
              <button type="button" className="btn-sm" onClick={() => navigate("/dashboard")}>
                Сегодня
              </button>
              <button type="button" className="btn-sm primary" onClick={() => navigate("/chat")}>
                Открыть чат
              </button>
            </div>
          </div>

          <div className="page">
            {psychologist ? (
              <BookingCalendar
                psychologist={psychologist}
                mode="PSYCHO"
                allowDayOff
              />
            ) : (
              <div className="panel">
                <p className="empty-note">Не удалось определить профиль психолога.</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
